import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import { MAILBOX_MESSAGE_QUERY_KEY } from '../components/message-detail/hooks/useMailboxMessage';
import { MAILBOX_MESSAGES_QUERY_KEY } from '../components/message-folder/hooks/useMailboxMessages';
import { MAILBOX_UNREAD_COUNT_QUERY_KEY } from './useMailboxUnreadCount';
import {
    buildMailboxFlagPayload,
    mapMailboxActionError,
} from '../helpers';

const useMailboxActions = ({
    t,
    onMoved,
}) => {
    const queryClient = useQueryClient();
    const service = useMailboxService();
    const [isActing, setIsActing] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const isSyncingRef = useRef(false);

    const refreshMailbox = async () => {
        await Promise.all([
            queryClient.invalidateQueries([MAILBOX_MESSAGES_QUERY_KEY]),
            queryClient.invalidateQueries([MAILBOX_MESSAGE_QUERY_KEY]),
            queryClient.invalidateQueries([MAILBOX_UNREAD_COUNT_QUERY_KEY]),
        ]);
    };

    const runAction = async (action, successMessage, moved = false) => {
        if (isActing) {
            return false;
        }

        setIsActing(true);

        try {
            await action();
            await refreshMailbox();

            if (successMessage) {
                toast.success(t(successMessage));
            }

            if (moved) {
                onMoved?.();
            }

            return true;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(mapMailboxActionError(error, t));
            return false;
        } finally {
            setIsActing(false);
        }
    };

    const updateFlags = async (ids, flags) => {
        const nextIds = (ids || []).filter(Boolean);

        if (!nextIds.length) {
            toast.info(t('Select at least one message.'));
            return false;
        }

        return runAction(async () => {
            if (nextIds.length === 1) {
                await service.updateMessageFlags(
                    nextIds[0],
                    buildMailboxFlagPayload(flags)
                );
                return;
            }

            await service.updateMessagesFlags(
                buildMailboxFlagPayload({
                    ids: nextIds,
                    ...flags,
                })
            );
        });
    };

    const trashMessages = async (ids) => {
        const nextIds = (ids || []).filter(Boolean);

        if (!nextIds.length) {
            toast.info(t('Select at least one message.'));
            return false;
        }

        return runAction(async () => {
            await Promise.all(nextIds.map((id) => service.trashMessage(id)));
        }, 'Message moved to Trash.', true);
    };

    const restoreMessages = async (ids) => {
        const nextIds = (ids || []).filter(Boolean);

        if (!nextIds.length) {
            toast.info(t('Select at least one message.'));
            return false;
        }

        return runAction(async () => {
            await Promise.all(nextIds.map((id) => service.restoreMessage(id)));
        }, 'Message restored.', true);
    };

    const syncMailbox = async ({ silent = false } = {}) => {
        if (isSyncingRef.current) {
            return false;
        }

        isSyncingRef.current = true;
        setIsSyncing(true);

        try {
            await service.syncMailbox();
            await refreshMailbox();

            if (!silent) {
                toast.success(t('Mailbox synced.'));
            }

            return true;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            if (!silent) {
                toast.error(mapMailboxActionError(error, t));
            }

            return false;
        } finally {
            isSyncingRef.current = false;
            setIsSyncing(false);
        }
    };

    return {
        isActing,
        isSyncing,
        updateFlags,
        trashMessages,
        restoreMessages,
        syncMailbox,
        refreshMailbox,
    };
};

export default useMailboxActions;
